<template>
    <div class="map-sidebar">

        <!-- If no cover image remove w-cover class -->
        <div class="user-lists-map__intro wrapper list-header">

            <div v-if="isMapBlocked" class="map-blocker filters" data-modal="join-the-club"></div>

            <a class="back-button" href="/lists">
                <svg class="chevron"><use xlink:href="#chevron"></use></svg>
                My Lists
            </a>

            <ul class="list-tabs" v-if="currentList.is_schedule_available">
                <li :class="{'active': currentTab == 'list'}">
                    <button type="button" @click="changeTab('list')">List</button>
                </li>
                <li :class="{'active': currentTab == 'schedule'}">
                    <button type="button" @click="changeTab('schedule')">Schedule</button>
                </li>
                <Tooltip :tooltips="tooltips" name="schedule-tab"></Tooltip>
            </ul>

            <div class="list-title">
                <h2 v-text="currentList.name"></h2>
                <div v-if="spots.length && ownsList" class="add-spot-to-list-popup">
                    <AddSpotToListPopup :list="currentList"></AddSpotToListPopup>
                    <button class="add-spot-btn" id="add-a-spot-to-list">
                        <svg class="plus"><use xlink:href="#plus"></use></svg>
                    </button>
                </div>
            </div>

            <p v-if="currentList.description" v-text="currentList.description"></p>

            <p v-if="ownsList && !currentList.description">
                <button @click="updateList()">Add a quick description</button>
            </p>

            <div class="extras">

                <!-- List Creator Information -->

                <creator-information :list="list"></creator-information>

                <div class="extras__buttons">

                    <!-- Follow List Button -->
                    <div class="button-wrap" v-if="showFollowButton">
                        <button @click="followListToggle()"
                                type="button"
                                class="follow"
                                title="Follow List"
                                :disabled="followingListRequest"
                                :class="{ 'display-tooltip' : showFollowToolTip }">
                            Follow <span>List</span>
                        </button>
                        <div class="tooltip">
                            Saves to your Lists and lets you know when this list has been updated.
                        </div>
                    </div>

                    <!-- Unfollow List Button -->
                    <div class="button-wrap" v-if="showUnfollowButton">
                        <button type="button" @click="followListToggle()" title="Unfollow List" class="unfollow" :disabled="followingListRequest">
                            Unfollow <span>List</span>
                        </button>
                    </div>

                    <!-- Duplicate List -->
                    <div class="button-wrap" v-if="!ownsList">

                        <Tooltip :tooltips="tooltips" name="duplicate"></Tooltip>

                        <button type="button" @click="duplicateList()" title="Duplicate List">
                            <svg class="duplicate"><use xlink:href="#duplicate"></use></svg>
                        </button>
                        <div class="tooltip" style="width: 200px; right: -60px;">
                            Duplicating a list will allow you to customize it and make it your own.
                        </div>
                    </div>

                    <!-- Copy List -->
                    <div class="button-wrap" v-if="!currentList.secret">

                        <Tooltip :tooltips="tooltips" name="share"></Tooltip>

                        <button type="button" class="list-copy-map-trigger" title="Copy List URL" :data-clipboard-text="currentList.copylink">
                            <svg class="copy"><use xlink:href="#copy"></use></svg>
                            <div class="save-button-tooltip list-copy">
                                <span>Copied</span>
                            </div>
                        </button>
                    </div>

                    <!-- Update -->
                    <div class="button-wrap" v-if="ownsList" @click="openEditModal()">
                        <button class="btn-icon" type="button" title="Edit">
                            <svg><use xlink:href="#pen"></use></svg>
                        </button>
                    </div>

                    <!-- Delete -->
                    <div class="button-wrap" v-if="ownsList" @click="openDeleteModal()">
                        <button  class="btn-icon" type="button" title="Delete">
                            <svg><use xlink:href="#delete"></use></svg>
                        </button>
                    </div>

                </div>
            </div>
        </div>

        <div class="map-list wrapper" v-show="currentTab == 'list'">

            <map-button-mobile :spots="spots"></map-button-mobile>

            <!-- Header -->
            <div class="map-list-header" v-if="spots.length">

                <!-- Destinations Text -->
                <div class="map-count">
                    <span v-text="destinationInformation"></span>
                </div>

                <!-- Only show this when there are more than one destination on the lists. -->
                <div class="map-sort" v-show="currentList && currentList.destinations && currentList.destinations.length > 1" :class="{'disabled' : isFiltering }">
                    <div class="map-sort-cta">
                        Show <span v-text="sortByLabel"></span>
                        <svg class="arrow-down"><use xlink:href="#arrow-down"></use></svg>
                    </div>
                    <div class="map-sort-dd">
                        <span v-if="filters.city_id" @click="filterByCity('')">All Destinations</span>
                        <span v-for="city in filterByCities"
                              :key="city.id"
                              v-if="filters.city_id != city.id"
                              @click="filterByCity(city)"
                              v-text="city.title">
                        </span>
                    </div>
                </div>

            </div>
            <div v-else-if="!ownsList" class="no-plans-yet">
                <p>No spots have been added to this list yet!</p>
            </div>

            <!-- Spots Body -->
            <div id="map-posts" v-if="spots.length">
                <draggable v-model="spots"
                           group="spots"
                           :disabled="(!ownsList || spots.length <= 1)"
                           handle=".draggable-trigger"
                           filter=".ignore-elements, .map-post__slider::before, .list-add-trigger"
                           :delay="150"
                           @change="draggedSpot">

                    <div class="map-post draggable-trigger"
                         v-for="(spot, index) in spots"
                         :about="spot.id"
                         :id="'map-post-'+spot.id"
                         @mouseover="mouseOver(spot)"
                         @touchstart="setCurrentSpot(spot)"
                         @mouseout="smallPin(spot)"
                         :key="spot.id">

                        <span v-if="!isMapBlocked && !ownsList"
                              class="post-user-lists-button list-add-trigger"
                              :data-id="spot.id"
                              :data-title="spot.title">
                            <svg class="save"><use xlink:href="#save"></use></svg>
                        </span>

                        <!-- Spot Slider -->
                        <map-spot-slider :spot="spot" ></map-spot-slider>

                        <!-- Spot Content -->
                        <div class="content">

                            <div class="action-buttons" v-if="ownsList">

                                <!-- Arrow Down Order -->
                                <button v-if="index > 0"
                                        @click="updateOrderDown(spot, index)"
                                        class="btn ignore-elements"
                                        style="transform: rotate(180deg)">
                                    <span>
                                        <svg class="arrow-down"><use xlink:href="#arrow-down"></use></svg>
                                    </span>
                                </button>

                                <!-- Arrow Up Order -->
                                <button v-if="index < (spots.length - 1)"
                                        @click="updateOrderUp(spot, index)"
                                        class="btn ignore-elements">
                                    <span>
                                        <svg class="arrow-down"><use xlink:href="#arrow-down"></use></svg>
                                    </span>
                                </button>

                                <Tooltip v-if="index == 0" :tooltips="tooltips" name="sort"></Tooltip>

                                <!-- Remove Spot -->
                                <button v-if="ownsList"
                                        @click="removeSpot(spot, index)"
                                        class="btn ignore-elements">
                                    <span>
                                        <svg class="delete"><use xlink:href="#delete"></use></svg>
                                    </span>
                                </button>
                            </div>

                            <!-- Spot Category -->
                            <h3 :class="`cat-${spot.category.slug}`">
                                <a :href="`/destination/${spot.city.slug}/browse?category=${spot.category.slug}`"
                                   v-text="spot.category.title">
                                </a>
                            </h3>

                            <!-- Spot Title -->
                            <h2>
                                <a :href="`/${spot.city.slug}/${spot.slug}`"
                                   v-text="spot.title">
                                </a>
                            </h2>

                            <div class="main-tags">
                                <h4>
                                    <span class="sub-cat" v-if="spot.sub_category">
                                        {{ spot.sub_category.title ?  spot.sub_category.title : ''  }}
                                    </span>
                                    <span v-if="spot.sub_category">in</span>
                                    <a  v-if="spot.neighborhood"
                                        :href="`/destination/${spot.city.slug}/browse?neighborhoods=${spot.neighborhood.slug}`"
                                        v-text="spot.neighborhood.title">
                                    </a>
                                    <a v-if="spot.neighborhood"
                                       :href="`/destination/${spot.city.slug}`"
                                       v-text="spot.city.title">
                                    </a>
                                </h4>
                                <p v-if="spot.category">
                                    <span v-text="spot.price_value "></span>
                                    <span v-text="spot.price_not_value"></span>
                                </p>
                            </div>
                            <div class="extra-tags" v-if="spot.tags.length">
                                <span v-for="tag in limitTags(spot.tags, 2)" v-text="tag.name.en"></span>
                            </div>
                        </div>

                        <map-spot-caption :index="index"
                                          :list="currentList"
                                          :spot="spot"
                                          :tooltips="tooltips">
                        </map-spot-caption>

                    </div>

                </draggable>
            </div>

            <!-- No Spots -->
            <div class="no-spots-container" v-else-if="ownsList">

                <div class="trip-img">
                    <svg class="trip"><use xlink:href="#trip"></use></svg>
                </div>

                <p>Add your first spot to the List!</p>

                <button class="add-spot-btn" id="add-a-spot-to-list">
                    <svg class="plus"><use xlink:href="#plus"></use></svg>
                </button>

            </div>

        </div>

        <!-- Sechedules -->
        <list-schedules :list="currentList"
                        :tooltips="tooltips"
                        v-show="currentTab == 'schedule'">
        </list-schedules>

        <AddSpotToListPopup v-if="spots.length == 0 && ownsList" :list="currentList"></AddSpotToListPopup>

    </div>
</template>

<script>

import MapButtonMobile from './MapButtonMobile'
import MapsHelpers from '@/mixins/MapsHelpers'
import Helpers from '@/mixins/Helpers'
import Draggable from 'vuedraggable'
import MapSpotSlider from "./Spots/Slider";
import MapSpotCaption from "./Spots/Caption";
import Tooltip from '@/components/Tooltip'
import Tooltips from '@/mixins/Tooltips'
import DuplicateList from '@/components/Lists/Duplicate'
import CreatorInformation from '@/components/Lists/CreatorInformation'
import AddSpotToListPopup from '@/components/Lists/AddToAList/PopupSlider'
import ListSchedules from '@/components/ListsSchedules/Index'

export default {

    mixins:[Helpers, MapsHelpers, Tooltips],

    props: ['list', 'cities', 'tooltips'],

    components: {
        Tooltip,
        Draggable,
        DuplicateList,
        MapSpotSlider,
        MapSpotCaption,
        MapButtonMobile,
        CreatorInformation,
        AddSpotToListPopup,
        ListSchedules
    },

    data() {
        return {
            currentTab: 'list',
            google: null,
            currentList: this.list,
            showFollowToolTip: false,
            isFiltering: false,
            followers: this.list.followers,
            followingListRequest: false,
            filters: {
                city_id: ''
            },
            selectedSpot: null,
            selectedCity: null,
            sortByLabel: "All Destinations",
            spots: [],
            deleteSpotIndex: null,
        };
    },

    mounted() {
        eventBus.$on('google-api-is-ready', api => {
            this.google = api
            this.fetchSpots()
        })
        this.chooseTab()
    },

    created() {

        eventBus.$on('list-updated', data => {
            this.currentList = data.list
            if(!this.currentList.is_schedule_available){
                this.currentTab = 'list';
            }
            history.pushState(null, null, data.url);
            eventBus.$emit('fetch-schedule')
        })

        if(this.tooltips.length) {
            eventBus.$on('tip-changed', tipNumber => {
                if (tipNumber == 5) {
                    this.currentTab = 'schedule'
                }
                if (tipNumber == 3) {
                    this.currentTab = 'list'
                }
            })
        }

        eventBus.$on('list-duplicated', data => this.redirectTo(data.url))

        eventBus.$on('move-scroll-to-spot', (spotId) => {
            var toremove = $('.main-header').outerHeight();
            $("html, body").animate({scrollTop: $(`#map-post-${spotId}`).offset().top - toremove - 12});
        });

        eventBus.$on('deleted-spot-from-list', list => {
            this.currentList = list;
            this.spots.splice(this.deleteSpotIndex, 1);
            this.fetchSpots();
        });

        eventBus.$on('list-deleted', data => {
            if (data.list.id == this.currentList.id) {
                this.redirectTo('/lists');
            }
        })

    },

    methods: {

        chooseTab() {
            var urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('tab') && this.currentList.is_schedule_available &&  config.isLoggedIn) {
                this.currentTab = urlParams.get('tab')
            }
        },

        changeTab(tab) {
            let currentUrl = window.location.origin + window.location.pathname;
            this.currentTab = tab
            var urlParams = new URLSearchParams("");
            urlParams.append('tab', tab);
            let newUrl = currentUrl + '?' + urlParams.toString()
            window.history.pushState(null, "", newUrl);
            if(tab == 'list'){
                eventBus.$emit("gotSpots", {spots: this.spots, autofocus: true});
            }
        },

        fetchSpots() {
            axios.post(`/list/${this.list.id}/spots`, this.filters)
                .then(response => {
                    this.spots = response.data.spots
                    this.selectedCity = response.data.city
                    this.isFiltering = false
                    eventBus.$emit("gotSpots", {spots: this.spots});
                    eventBus.$emit('checkSpotsInList')
                })
        },

        limitTags(tags, number) {
            return tags.slice(0, number);
        },

        updateOrderDown(spot, index) {
            this.draggedSpot({
                moved: {
                    element: spot,
                    newIndex: index - 1
                }
            })
            this.spots.splice(index - 1, 0, this.spots.splice(index, 1)[0])
        },

        updateOrderUp(spot, index) {
            this.draggedSpot({
                moved: {
                    element: spot,
                    newIndex: index + 1
                }
            })
            this.spots.splice(index + 1, 0, this.spots.splice(index, 1)[0])
        },

        draggedSpot(event) {
            let spot = event.moved.element;
            let order = event.moved.newIndex + 1;
            axios.patch(`/lists/${this.list.id}/spot/${spot.slug}/sort`, {
                order: order,
            })
                .then(response => {
                    // console.log('OK')
                })
        },

        updateList() {
            eventBus.$emit('open-update-list-modal', this.currentList);
        },

        duplicateList() {
            eventBus.$emit('open-duplicate-list-modal', this.currentList);
        },

        removeSpot(spot, index) {
            this.deleteSpotIndex = index;
            eventBus.$emit('open-delete-spot-from-list-modal', {
                list: this.currentList,
                spot: spot
            });
        },

        followListToggle() {
            this.followingListRequest = true;
            axios.post('/lists/follow', {list_id: this.currentList.id})
                .then((response) => {
                    if (response.data.status == 'error') {
                        this.errorMessage = response.data.content;
                    } else {
                        this.followers = response.data.followers;
                    }
                    this.followingListRequest = false;
                })
                .catch(errors => {
                    this.followingListRequest = false;
                })
        },

        mouseOver(spot) {
            this.setCurrentSpot(spot);
            this.enlargePin(spot)
        },

        enlargePin(spot) {
            eventBus.$emit('markerBigger', spot);
        },

        smallPin(spot) {
            eventBus.$emit('markerSmall', spot);
        },

        setCurrentSpot(spot) {
            this.currentSpot = spot;
        },

        filterByCity(city) {
            if(this.isFiltering){
                return
            }

            this.isFiltering = true
            $(".map-sort-dd").hide();
            setTimeout(function () {
                $(".map-sort-dd").show()
            }, 300);
            this.sortByLabel = city ? city.title : 'All Destinations';
            this.filters.city_id = city ? city.id : null
            this.fetchSpots();
        },

        openEditModal() {
            eventBus.$emit('open-update-list-modal', this.currentList);
        },

        openDeleteModal() {
            eventBus.$emit('open-delete-list-modal', this.currentList);
        },
    },

    computed: {

        showFollowButton() {
            return !this.ownsList && !this.currentList.secret && !this.isFollowed
        },

        showUnfollowButton() {
            return !this.ownsList && !this.currentList.secret && this.isFollowed
        },

        isFollowed() {
            if (!window.config.isLoggedIn) return false;
            return !!_.find(this.followers, user => {
                return user.id == window.config.user.id
            })
        },

        ownsList() {
            return window.config.isLoggedIn && window.config.user.id == this.currentList.user_id;
        },

        isTouchDevice() {
            return ('ontouchstart' in window || navigator.msMaxTouchPoints);
        },

        isMapBlocked() {
            return window.config.isLoggedIn == false
        },

        filterByCities() {
            return this.currentList.destinations;
        },

        destinationInformation() {
            if (this.selectedCity) {
                return this.spots.length + ' ' + this.pluralize('Spots', this.spots.length) + ' in ' + this.selectedCity.title
            }
            return this.currentList.destinations_text
        },

    },

    watch:{
        currentTab(){
            if(this.currentTab == 'schedule') {
                eventBus.$emit('reload-slick')
            }
        }
    }
}
</script>
<style>
.sortable-chosen {
    opacity: 0.5;
    background: #dcdcdc;
    border-radius: 4px;
    cursor: grab;
    border: 3px dashed #7d7979;
}
</style>
